import { useEffect, useState } from 'react';
import EmbedPlayer, { detectEmbedType } from './EmbedPlayer';
import type { PlayerInfo, SoundElements } from '../types';
import { detectPlatformFromUrl, getStaticThumbnail, fetchOEmbedThumbnail } from '../utils/platformUtils';

// ─────────────────────────────────────────────
// 共通：レイヤー未登録時の DIVE（浮遊感）表示
// ─────────────────────────────────────────────
interface EmptyLayerProps {
  accentColor: string;
  layerLabel: string;   // e.g. "Layer 02 / Player"
  icon: string;         // Remix Icon class
  message?: string;
}

export function EmptyLayer({ accentColor, layerLabel, icon, message = 'このレイヤーはまだ登録されていません' }: EmptyLayerProps) {
  return (
    <div className="mb-12">
      {/* Layer ラベル */}
      <div className="flex items-center gap-3 mb-4">
        <div
          className="w-2 h-2 rounded-full"
          style={{ background: accentColor, boxShadow: `0 0 10px ${accentColor}` }}
        />
        <p className="text-gray-500 text-xs tracking-widest uppercase">{layerLabel}</p>
      </div>

      {/* 浮遊感コンテナ */}
      <div
        className="relative rounded-2xl overflow-hidden border border-dashed flex flex-col items-center justify-center gap-4 py-14"
        style={{
          borderColor: `${accentColor}25`,
          background: `radial-gradient(ellipse at 50% 60%, ${accentColor}08 0%, transparent 70%)`,
        }}
      >
        {/* 背景の光の粒 */}
        {[...Array(6)].map((_, i) => (
          <div
            key={i}
            className="absolute rounded-full pointer-events-none"
            style={{
              width: `${4 + (i % 3) * 3}px`,
              height: `${4 + (i % 3) * 3}px`,
              background: accentColor,
              opacity: 0.12 + (i % 3) * 0.06,
              top: `${15 + i * 12}%`,
              left: `${10 + i * 14}%`,
              filter: `blur(${1 + (i % 2)}px)`,
              animation: `float-${i % 3} ${3 + i * 0.7}s ease-in-out infinite alternate`,
            }}
          />
        ))}

        {/* 中央アイコン */}
        <div
          className="relative w-16 h-16 rounded-full flex items-center justify-center"
          style={{
            background: `radial-gradient(circle, ${accentColor}18 0%, ${accentColor}06 100%)`,
            border: `1px solid ${accentColor}30`,
            boxShadow: `0 0 30px ${accentColor}20, inset 0 0 20px ${accentColor}08`,
          }}
        >
          <i className={`${icon} text-2xl`} style={{ color: `${accentColor}70` }} />
          {/* 外周リング */}
          <div
            className="absolute inset-0 rounded-full"
            style={{
              border: `1px solid ${accentColor}15`,
              transform: 'scale(1.4)',
            }}
          />
        </div>

        {/* テキスト */}
        <div className="flex flex-col items-center gap-1.5 z-10">
          <p
            className="text-sm font-bold tracking-[0.25em] uppercase"
            style={{ color: `${accentColor}60` }}
          >
            NOT REGISTERED
          </p>
          <p className="text-gray-600 text-[11px] tracking-wider">{message}</p>
        </div>
      </div>
    </div>
  );
}

interface LayerPlayerProps {
  accentColor: string;
  playerInfo: PlayerInfo | null;
  hasPlayerLayer: boolean;
  isAutoplay?: boolean;
  onLinkPlay?: () => void;
  compact?: boolean;
}

function useSnsThumb(url: string | undefined): string | null {
  const [thumb, setThumb] = useState<string | null>(null);
  useEffect(() => {
    if (!url) {
      setThumb(null);
      return;
    }
    const embedType = detectEmbedType(url);
    if (embedType !== 'none') {
      setThumb(null);
      return;
    }
    const staticThumb = getStaticThumbnail(url);
    if (staticThumb) {
      setThumb(staticThumb);
      return;
    }
    let cancelled = false;
    fetchOEmbedThumbnail(url).then((t) => {
      if (!cancelled) setThumb(t);
    });
    return () => {
      cancelled = true;
    };
  }, [url]);
  return thumb;
}

function SoundElementTags({ soundElements }: { soundElements: SoundElements | null }) {
  if (!soundElements) return null;

  const categories = [
    {
      key: 'style',
      label: 'スタイル',
      items: soundElements.style || [],
      color: '#a855f7',
      icon: 'ri-palette-line',
    },
    {
      key: 'energy',
      label: '熱量',
      items: soundElements.energy || [],
      color: '#f97316',
      icon: 'ri-flashlight-line',
    },
    {
      key: 'texture',
      label: '手触り',
      items: soundElements.texture || [],
      color: '#06b6d4',
      icon: 'ri-ripple-line',
    },
  ].filter((cat) => cat.items.length > 0);

  if (categories.length === 0) return null;

  return (
    <div>
      <p className="text-gray-300 text-[10px] tracking-widest uppercase mb-1">音の成分</p>
      <div className="grid grid-cols-3 grid-rows-1 gap-x-3 gap-y-2">
        {categories.map((cat) => (
          <div key={cat.key} className="flex flex-col gap-1">
            {/* カテゴリラベル */}
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 flex items-center justify-center">
                <i className={`${cat.icon} text-[9px]`} style={{ color: `${cat.color}cc` }} />
              </div>
              <span
                className="text-[9px] font-bold tracking-[0.1em]"
                style={{ color: `${cat.color}cc` }}
              >
                {cat.label}
              </span>
            </div>
            {/* タグ */}
            <div className="flex flex-wrap gap-1">
              {cat.items.map((item, idx) => (
                <span
                  key={idx}
                  className="inline-flex items-center px-2 py-0.5 text-[11px] font-medium tracking-wide rounded-sm"
                  style={{
                    background: `${cat.color}18`,
                    color: `${cat.color}ff`,
                    borderLeft: `2px solid ${cat.color}99`,
                  }}
                >
                  {item}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function WaveformBars({ accentColor, count }: { accentColor: string; count: number }) {
  return (
    <div className="flex items-center gap-2 h-full">
      {Array.from({ length: count }).map((_, i) => {
        const height = 20 + Math.sin(i * 0.3) * 30 + Math.random() * 50;
        return (
          <div
            key={i}
            className="flex-1 rounded-sm transition-all duration-300 cursor-pointer"
            style={{
              height: `${height}%`,
              minHeight: '8px',
              background: `linear-gradient(to top, ${accentColor}, ${accentColor}50)`,
              boxShadow: `0 0 8px ${accentColor}50`,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = `linear-gradient(to top, white, ${accentColor})`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = `linear-gradient(to top, ${accentColor}, ${accentColor}50)`;
            }}
          />
        );
      })}
    </div>
  );
}

export function LayerPlayer({
  accentColor,
  playerInfo,
  hasPlayerLayer,
  isAutoplay = false,
  onLinkPlay,
  compact = false,
}: LayerPlayerProps) {
  const snsThumbUrl = useSnsThumb(playerInfo?.playerSnsUrl);
  const playerEmbedType = detectEmbedType(playerInfo?.playerSnsUrl || '');

  if (!hasPlayerLayer || !playerInfo) {
    return (
      <EmptyLayer
        accentColor={accentColor}
        layerLabel="Layer 02 / Player"
        icon="ri-music-2-line"
      />
    );
  }

  const platform = playerInfo.playerSnsUrl ? detectPlatformFromUrl(playerInfo.playerSnsUrl) : null;
  const waveCount = compact ? 60 : 100;
  const waveHeight = compact ? 'h-20' : 'h-24';
  const thumbHeight = compact ? 'h-36' : 'h-44';
  const playBtnSize = compact ? 'w-10 h-10' : 'w-12 h-12';
  const playIconSize = compact ? 'text-lg' : 'text-xl';
  const trackNameSize = compact ? 'text-sm' : 'text-base';
  const trackLabelSize = compact ? 'text-xs' : 'text-xs';
  const padding = compact ? 'p-4' : 'p-5';

  return (
    <div className="mb-6">
      <div className="flex items-center gap-3 mb-3">
        <div
          className="w-2 h-2 rounded-full"
          style={{ background: accentColor, boxShadow: `0 0 10px ${accentColor}` }}
        />
        <p className="text-gray-500 text-xs tracking-widest uppercase">Layer 02 / Player</p>
      </div>
      <div
        className={`bg-gradient-to-r from-[#1a1a1a] to-[#0f0f0f] rounded-2xl px-5 pt-3 pb-4 border`}
        style={{ borderColor: `${accentColor}30`, boxShadow: `0 0 30px ${accentColor}20` }}
      >
        {/* Sound Element Tags — 埋め込みの上に表示 */}
        {playerInfo.soundElements && (
          <div className="mb-2">
            <SoundElementTags soundElements={playerInfo.soundElements} />
          </div>
        )}

        {/* Embeddable player */}
        {playerEmbedType !== 'none' && playerInfo.playerSnsUrl ? (
          <EmbedPlayer url={playerInfo.playerSnsUrl} accentColor={accentColor} autoplay={isAutoplay} />
        ) : playerInfo.trackName ? (
          <div className={`${waveHeight} mb-4`}>
            <WaveformBars accentColor={accentColor} count={waveCount} />
          </div>
        ) : (
          <a
            href={playerInfo.playerSnsUrl}
            target="_blank"
            rel="nofollow noopener noreferrer"
            onClick={onLinkPlay}
            className={`block relative w-full ${thumbHeight} rounded-xl overflow-hidden mb-4 group cursor-pointer`}
          >
            {snsThumbUrl ? (
              <img
                src={snsThumbUrl}
                alt="SNS Thumbnail"
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
              />
            ) : (
              <div
                className="w-full h-full flex items-center justify-center"
                style={{ background: `linear-gradient(135deg, ${accentColor}15, ${accentColor}05)` }}
              >
                <div
                  className="w-20 h-20 rounded-full flex items-center justify-center border"
                  style={{ borderColor: `${accentColor}40`, background: `${accentColor}10` }}
                >
                  <i className="ri-music-2-fill text-3xl" style={{ color: `${accentColor}80` }} />
                </div>
              </div>
            )}
            <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
              <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center border border-white/30">
                <i className="ri-external-link-line text-2xl text-white" />
              </div>
            </div>
          </a>
        )}

        {/* Track info + link */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {playerInfo.trackName && playerEmbedType === 'none' && (
              <button
                onClick={onLinkPlay}
                className={`${playBtnSize} rounded-full border flex items-center justify-center transition-all duration-300 cursor-pointer whitespace-nowrap`}
                style={{
                  background: `${accentColor}20`,
                  borderColor: accentColor,
                  boxShadow: `0 0 20px ${accentColor}30`,
                }}
              >
                <i className={`ri-play-fill ${playIconSize}`} style={{ color: accentColor }} />
              </button>
            )}
            <div>
              {playerInfo.trackName ? (
                <>
                  <p className={`text-white font-bold ${trackNameSize}`}>{playerInfo.trackName}</p>
                  <p className={`text-gray-400 ${trackLabelSize}`}>{playerInfo.trackLabel}</p>
                </>
              ) : platform ? (
                <p className={`text-white font-bold ${trackNameSize}`}>{platform.name}</p>
              ) : null}
            </div>
          </div>

          <div className="flex items-center gap-3">
            {platform && playerInfo.playerSnsUrl && playerEmbedType === 'none' && (
              <a
                href={playerInfo.playerSnsUrl}
                target="_blank"
                rel="nofollow noopener noreferrer"
                onClick={onLinkPlay}
                className="flex items-center gap-2 px-3 py-2 rounded-full border transition-all duration-300 cursor-pointer hover:scale-105 group"
                style={{ background: `${platform.color}15`, borderColor: `${platform.color}40` }}
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`${platform.icon} text-sm`} style={{ color: platform.color }} />
                </div>
                <span className="text-xs font-medium tracking-wider" style={{ color: platform.color }}>
                  {platform.name}
                </span>
                <i
                  className="ri-external-link-line text-xs opacity-60 group-hover:opacity-100 transition-opacity"
                  style={{ color: platform.color }}
                />
              </a>
            )}
            {playerInfo.trackName && playerEmbedType === 'none' && (
              <div className="text-gray-500 text-xs">
                <span className="text-white">0:00</span> / {playerInfo.duration}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
